import { Box, Skeleton, Text, VStack } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import FlexSearch from "flexsearch";
import { memo, useCallback } from "react";
import { FeaturedSection } from "./featured-section/table";
import { TableContent } from "./tables/table-content";
import { TableRow } from "./utils/tables-metadata";
import { useSharedContext } from "../shared-logic/shared-context";

export type PreviewSectionCounts = {
  sectionCounts: {
    reports: number;
    documents: number;
  };
};

export type IndexedTableData = {
  tableType: string;
  displayName: string;
  rows: TableRow[];
  index: FlexSearch.Index;
  indexingPromises: Promise<FlexSearch.Index>[];
  isEmptySection: boolean;
};

export type FilteredTableData = {
  tableType: string;
  displayName: string;
  rows: TableRow[];
  index: FlexSearch.Index;
  indexingPromises: Promise<FlexSearch.Index>[];
  filteredRows: TableRow[];
  isEmptySection: boolean;
};

interface PatientOverviewContentV2Props {
  fhirSdk: FhirBundleSdk | undefined;
  children: React.ReactNode;
  isLoadingData?: boolean;
  filterQuery?: string;
  filteredTableData: FilteredTableData[];
  onFeaturedDataChange?: (hasFeaturedData: boolean) => void;
  onRowSelect: (rowState: SelectedRowState) => void;
  onPdfView: (docId: string, docFilename?: string, initialSearchQuery?: string) => void;
  isRowViewed: (tableType: string, rowId: string) => boolean;
  onDocumentDownloaded?: (docId: string) => void;
}

export type SelectedRowState = {
  row: TableRow;
  tableType: string;
  suspectsContext?: string;
};

function PatientOverviewContentV2({
  fhirSdk,
  children,
  filterQuery = "",
  isLoadingData = false,
  filteredTableData,
  onFeaturedDataChange,
  onRowSelect,
  onPdfView,
  isRowViewed,
  onDocumentDownloaded,
}: PatientOverviewContentV2Props) {
  const {
    sharedState: { isAmazonUser, isMetriportUser },
  } = useSharedContext();
  /**
   * Handles row click from any table.
   * Notifies parent to handle side panel opening and selection.
   */
  const handleRowClick = useCallback(
    (row: TableRow, tableType: string, suspectsContext?: string) => {
      onRowSelect({ row, tableType, suspectsContext });
    },
    [onRowSelect]
  );

  if (!fhirSdk || isLoadingData) {
    return (
      <Box minH="100vh" backgroundColor="bg.canvas">
        <VStack align="stretch" gap={4}>
          <Skeleton height="400px" borderRadius={10} />
          <Skeleton height="300px" borderRadius={10} />
          <Skeleton height="350px" borderRadius={10} />
          <Skeleton height="250px" borderRadius={10} />
        </VStack>
      </Box>
    );
  }

  const isUserSearching = !!filterQuery.trim();
  const allSectionsEmpty = filteredTableData.every(table => table.isEmptySection);

  return (
    <>
      <Box backgroundColor="bg.canvas">
        <VStack align="stretch">
          <Box id="ai-summary">{children}</Box>
          <VStack align="stretch">
            {(isAmazonUser || isMetriportUser) && (
              <Box id="featured">
                <FeaturedSection
                  filteredTableData={filteredTableData}
                  onRowClick={handleRowClick}
                  fhirSdk={fhirSdk}
                  filterQuery={filterQuery}
                  onPdfView={onPdfView}
                  onFeaturedDataChange={onFeaturedDataChange}
                  isRowViewed={isRowViewed}
                />
              </Box>
            )}

            {allSectionsEmpty && (
              <Box
                py={10}
                px={8}
                borderRadius="lg"
                backgroundColor="bg.subtle"
                border="1px"
                borderColor="border"
                textAlign="center"
              >
                <Text fontWeight="semibold" color="fg.muted" fontSize="lg">
                  {isUserSearching ? "No results found" : "No patient data available"}
                </Text>
                <Text color="fg.subtle" fontSize="sm" mt={2}>
                  {isUserSearching
                    ? "Try different keywords, filters, or review your spelling."
                    : "This patient does not have any medical records yet."}
                </Text>
              </Box>
            )}

            {filteredTableData.map(({ tableType, displayName, filteredRows, isEmptySection }) => {
              if (isEmptySection) {
                return null;
              }

              return (
                <Box
                  key={tableType}
                  id={tableType}
                  w="100%"
                  mb={2}
                  borderRadius="lg"
                  bg="bg.surface"
                  px={6}
                  py={6}
                  border="1px"
                  borderColor="border"
                  boxShadow="sm"
                >
                  <TableContent
                    tableType={tableType}
                    displayName={displayName}
                    filteredRows={filteredRows}
                    fhirSdk={fhirSdk}
                    filterQuery={filterQuery}
                    handleRowClick={handleRowClick}
                    handlePdfView={onPdfView}
                    isRowViewed={isRowViewed}
                    onDocumentDownloaded={onDocumentDownloaded}
                  />
                </Box>
              );
            })}
          </VStack>
        </VStack>
      </Box>
    </>
  );
}

export default memo(PatientOverviewContentV2);
